import {
  collection,
  doc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  writeBatch,
  type CollectionReference,
  type FirestoreError,
  type QueryConstraint,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { eventDocumentConverter } from "@kekkle/shared/convertors/web/event-document.converter";
import type { EventDocument } from "@kekkle/shared/types/documents/event-document/event-document";
import type { EventSearchParams } from "@/types/firestore/event";

/**
 * Returns a reference to the events collection for a specific group with converter
 */
function eventsCollection(
  groupDocId: string,
): CollectionReference<EventDocument> {
  return collection(firestore, `groups/${groupDocId}/events`).withConverter(
    eventDocumentConverter,
  );
}

import type { SnapshotMetadata } from "@/types/firestore/metadata";
export type { SnapshotMetadata };

/**
 * Subscribes to all events for a group, ordered by begin_timestamp ascending.
 * Uses onSnapshot for real-time updates and persistent local caching.
 */
export function subscribeToEvents(
  groupId: string,
  callbacks: {
    onEvents: (events: EventDocument[], metadata: SnapshotMetadata) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  const q = query(eventsCollection(groupId), orderBy("begin_timestamp", "asc"));
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      callbacks.onEvents(
        snapshot.docs.map((doc) => doc.data()),
        {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
        },
      );
    },
    (error) => {
      callbacks.onError(error);
    },
  );
}

/**
 * Fetches events based on query constraints
 */
async function fetchEvents(
  groupId: string,
  queryConstraints: QueryConstraint[],
): Promise<EventDocument[]> {
  const q = query(eventsCollection(groupId), ...queryConstraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * Fetches events within a specific date range
 */
export async function fetchEventsByDateRange(
  groupId: string,
  startDate: Date,
  endDate: Date,
): Promise<EventDocument[]> {
  const queryConstraints: QueryConstraint[] = [
    where("begin_timestamp", "<", endDate),
    where("begin_timestamp", ">", startDate),
  ];
  return await fetchEvents(groupId, queryConstraints);
}

/**
 * Deletes a specific event by its ID
 */
export async function deleteEventById(
  eventId: string,
  groupId: string,
): Promise<void> {
  if (!groupId) {
    throw new Error("Need group Id");
  }

  const eventRef = doc(eventsCollection(groupId), eventId);
  const batch = writeBatch(firestore);
  batch.delete(eventRef);

  const groupCollection = collection(firestore, "groups/");
  const groupRef = doc(groupCollection, groupId);
  batch.update(groupRef, {
    "general_data.number_of_events": increment(-1),
  });

  const icalRef = doc(
    collection(firestore, `groups/${groupId}/settings`),
    "ical",
  );
  batch.set(icalRef, { needs_update: true }, { merge: true });

  await batch.commit();
}

/**
 * Adds a new event in the group
 */
export async function addEvent(
  groupId: string,
  data: Omit<EventDocument, "_id">,
): Promise<void> {
  const groupCollection = collection(firestore, "groups/");
  const groupRef = doc(groupCollection, groupId);
  const batch = writeBatch(firestore);
  const eventsRef = eventsCollection(groupId);
  const eventRef = doc(eventsRef);
  batch.set(eventRef, data as EventDocument);

  batch.update(groupRef, {
    "general_data.number_of_events": increment(1),
  });

  const icalRef = doc(
    collection(firestore, `groups/${groupId}/settings`),
    "ical",
  );
  batch.set(icalRef, { needs_update: true }, { merge: true });

  await batch.commit();
}

/**
 * Updates a specific event by its ID
 */
export async function updateEventById(
  eventId: string,
  groupId: string,
  data: Partial<Omit<EventDocument, "_id">>,
): Promise<void> {
  if (!groupId) {
    throw new Error("Need group Id");
  }

  const eventRef = doc(eventsCollection(groupId), eventId);
  const batch = writeBatch(firestore);
  batch.update(eventRef, data);

  const icalRef = doc(
    collection(firestore, `groups/${groupId}/settings`),
    "ical",
  );
  batch.set(icalRef, { needs_update: true }, { merge: true });

  await batch.commit();
}

/**
 * Unified search method for events with flexible filtering
 * Supports pagination and date filtering
 */
export async function searchEventsWithFilters(
  groupId: string,
  searchParams: EventSearchParams = {},
): Promise<EventDocument[]> {
  const { date, pageSize = 10, lastEvent, direction = "asc" } = searchParams;

  const queryConstraints: QueryConstraint[] = [];

  // Handle date filtering
  if (date) {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    queryConstraints.push(where("begin_timestamp", ">=", startOfDay));
    queryConstraints.push(where("begin_timestamp", "<=", endOfDay));
    queryConstraints.push(orderBy("begin_timestamp", direction));
  } else {
    queryConstraints.push(orderBy("begin_timestamp", direction));
  }

  // Handle pagination
  if (lastEvent) {
    queryConstraints.push(startAfter(lastEvent.begin_timestamp));
  }

  queryConstraints.push(limit(pageSize));

  return await fetchEvents(groupId, queryConstraints);
}
